import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, Area, AreaChart } from 'recharts';
import { monteCarloApi, accountsApi, Account, MonteCarloResult } from '../services/api';
import { useToast } from '../components/Toast';

const s = {
  page: { padding: 0 } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  badge: { background: '#f59e0b', color: '#000', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 } as React.CSSProperties,
  btn: { background: '#f59e0b', color: '#000', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 } as React.CSSProperties,
  card: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 12, padding: '18px 20px' } as React.CSSProperties,
  cardLabel: { color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 8 } as React.CSSProperties,
  cardValue: { fontSize: 24, fontWeight: 700, color: '#e2e8f0' } as React.CSSProperties,
  cardSub: { color: '#475569', fontSize: 11, marginTop: 4 } as React.CSSProperties,
  chart: { background: '#13172b', padding: 24, borderRadius: 14, border: '1px solid #1e2539', marginBottom: 24 } as React.CSSProperties,
  chartTitle: { color: '#e2e8f0', marginBottom: 20, fontSize: 16, fontWeight: 700 } as React.CSSProperties,
  loading: { display: 'flex', alignItems: 'center', gap: 12, padding: 60, color: '#64748b', justifyContent: 'center' } as React.CSSProperties,
  infoBox: { background: '#0f172a', border: '1px solid #f59e0b44', borderRadius: 12, padding: 20, marginBottom: 24 } as React.CSSProperties,
};

const MonteCarlo: React.FC = () => {
  const toast = useToast();
  const [account, setAccount] = useState<Account | null>(null);
  const [result, setResult] = useState<MonteCarloResult | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const accRes = await accountsApi.list();
      const active = accRes.data.find((a: Account) => a.is_active);
      if (!active) { setLoading(false); return; }
      setAccount(active);
      const mcRes = await monteCarloApi.simulate(active.id);
      setResult(mcRes.data);
    } catch (e: any) {
      if (e.response?.status !== 404) toast.error('Monte Carlo fehlgeschlagen', e.message);
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div style={s.loading}><span>🎲 Monte Carlo Simulation laeuft...</span></div>;
  if (!account) return <div style={s.loading}>Kein aktives Konto.</div>;
  if (!result) return (
    <div style={s.page}>
      <h1 style={s.title}>🎲 Monte Carlo</h1>
      <div style={s.infoBox}>
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Mindestens 5 Trades noetig fuer Monte Carlo Simulation.</p>
      </div>
    </div>
  );

  const r = result;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>
          🎲 Monte Carlo Simulation
          <span style={s.badge}>{account.name}</span>
          <span style={{ fontSize: 14, fontWeight: 400, color: '#64748b' }}>
            {r.simulations} Simulationen · {r.trades_used} Trades
          </span>
        </h1>
        <button style={s.btn} onClick={loadData}>🔄 Neu simulieren</button>
      </div>

      {/* Info Box */}
      <div style={s.infoBox}>
        <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 8, fontSize: 14 }}>
          Was zeigt Monte Carlo?
        </div>
        <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          Deine {r.trades_used} echten Trades werden {r.simulations}x zufaellig neu angeordnet.
          Das zeigt alle moeglichen Szenarien die mit deiner aktuellen Performance eintreten koennten.
        </p>
      </div>

      {/* Key Metrics */}
      <div style={s.grid}>
        <div style={{ ...s.card, borderLeft: r.survival_rate >= 70 ? '3px solid #22c55e' : '3px solid #ef4444' }}>
          <div style={s.cardLabel}>Survival Rate</div>
          <div style={{ ...s.cardValue, color: r.survival_rate >= 70 ? '#22c55e' : '#ef4444' }}>
            {r.survival_rate.toFixed(1)}%
          </div>
          <div style={s.cardSub}>Wahrscheinlichkeit profitabel zu sein</div>
        </div>
        <div style={{ ...s.card, borderLeft: r.ruin_risk < 10 ? '3px solid #22c55e' : '3px solid #ef4444' }}>
          <div style={s.cardLabel}>Ruin Risiko</div>
          <div style={{ ...s.cardValue, color: r.ruin_risk < 10 ? '#22c55e' : '#ef4444' }}>
            {r.ruin_risk.toFixed(1)}%
          </div>
          <div style={s.cardSub}>Equity unter 50% von Start</div>
        </div>
        <div style={{ ...s.card, borderLeft: '3px solid #3b82f6' }}>
          <div style={s.cardLabel}>Median Equity</div>
          <div style={s.cardValue}>${r.median_final_equity.toFixed(0)}</div>
          <div style={s.cardSub}>50% Wahrscheinlichkeit</div>
        </div>
        <div style={{ ...s.card, borderLeft: '3px solid #22c55e' }}>
          <div style={s.cardLabel}>Best Case (95%)</div>
          <div style={{ ...s.cardValue, color: '#22c55e' }}>${r.best_case_equity.toFixed(0)}</div>
          <div style={s.cardSub}>Obere 5% der Simulationen</div>
        </div>
        <div style={{ ...s.card, borderLeft: '3px solid #ef4444' }}>
          <div style={s.cardLabel}>Worst Case (5%)</div>
          <div style={{ ...s.cardValue, color: '#ef4444' }}>${r.worst_case_equity.toFixed(0)}</div>
          <div style={s.cardSub}>Untere 5% der Simulationen</div>
        </div>
        <div style={{ ...s.card, borderLeft: '3px solid #f59e0b' }}>
          <div style={s.cardLabel}>Worst Drawdown (95%)</div>
          <div style={{ ...s.cardValue, color: '#f59e0b' }}>{r.worst_case_drawdown.toFixed(1)}%</div>
          <div style={s.cardSub}>Maximaler realistischer DD</div>
        </div>
      </div>

      {/* Equity Range */}
      <div style={{ ...s.card, marginBottom: 24, padding: 20 }}>
        <div style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Equity Verteilung nach {r.trades_used} Trades</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: '#ef4444' }}>${r.p5_equity.toFixed(0)}</span>
          <div style={{ flex: 1, height: 24, background: '#1e2539', borderRadius: 12, overflow: 'hidden', position: 'relative' as const }}>
            <div style={{ position: 'absolute' as const, left: `${Math.max(0, ((r.p5_equity - r.worst_case_equity) / (r.best_case_equity - r.worst_case_equity)) * 100)}%`, right: `${Math.max(0, 100 - ((r.p95_equity - r.worst_case_equity) / (r.best_case_equity - r.worst_case_equity)) * 100)}%`, top: 0, bottom: 0, background: '#ef444444', borderRadius: 12 }} />
            <div style={{ position: 'absolute' as const, left: `${Math.max(0, ((r.p25_equity - r.worst_case_equity) / (r.best_case_equity - r.worst_case_equity)) * 100)}%`, right: `${Math.max(0, 100 - ((r.p75_equity - r.worst_case_equity) / (r.best_case_equity - r.worst_case_equity)) * 100)}%`, top: 2, bottom: 2, background: '#3b82f6', borderRadius: 10 }} />
            <div style={{ position: 'absolute' as const, left: `${((r.median_final_equity - r.worst_case_equity) / (r.best_case_equity - r.worst_case_equity)) * 100}%`, top: 0, bottom: 0, width: 3, background: '#fff', borderRadius: 2 }} />
          </div>
          <span style={{ color: '#22c55e' }}>${r.p95_equity.toFixed(0)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: '#64748b' }}>
          <span>5% Worst</span><span>25%</span><span>Median: ${r.median_final_equity.toFixed(0)}</span><span>75%</span><span>95% Best</span>
        </div>
      </div>

      {/* Confidence Bands Chart */}
      <div style={s.chart}>
        <h2 style={s.chartTitle}>Equity Confidence Bands (1000 Simulationen)</h2>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={r.confidence_bands}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
            <XAxis dataKey="trade_num" tick={{ fill: '#64748b', fontSize: 11 }} label={{ value: 'Trade Nummer', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 12 }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} label={{ value: 'Equity ($)', angle: -90, position: 'insideLeft', offset: 10, fill: '#64748b', fontSize: 12 }} />
            <Tooltip contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
              formatter={(v: any, name: string) => [`$${Number(v).toFixed(0)}`, name]} />
            <Area type="monotone" dataKey="p5" stackId="bg" stroke="none" fill="#ef444415" name="5% Worst" />
            <Area type="monotone" dataKey="p25" stackId="bg2" stroke="none" fill="#3b82f615" name="25%" />
            <Area type="monotone" dataKey="p75" stackId="bg3" stroke="none" fill="#3b82f615" name="75%" />
            <Area type="monotone" dataKey="p95" stackId="bg4" stroke="none" fill="#22c55e15" name="95% Best" />
            <Line type="monotone" dataKey="p50" stroke="#f59e0b" strokeWidth={2} dot={false} name="Median" />
            <Line type="monotone" dataKey="p5" stroke="#ef4444" strokeWidth={1} strokeDasharray="4 4" dot={false} name="5%" />
            <Line type="monotone" dataKey="p95" stroke="#22c55e" strokeWidth={1} strokeDasharray="4 4" dot={false} name="95%" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Sample Curves */}
      <div style={s.chart}>
        <h2 style={s.chartTitle}>10 Zufaellige Simulationen</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
            <XAxis tick={{ fill: '#64748b', fontSize: 11 }} label={{ value: 'Trade Nummer', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 12 }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} label={{ value: 'Equity ($)', angle: -90, position: 'insideLeft', offset: 10, fill: '#64748b', fontSize: 12 }} />
            <Tooltip contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }} />
            {r.sample_curves.map((curve, i) => (
              <Line key={i} data={curve.map((eq, j) => ({ x: j, y: eq }))} type="monotone"
                dataKey="y" stroke={['#22c55e','#3b82f6','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#ec4899','#14b8a6','#f97316','#6366f1'][i]}
                strokeWidth={1} dot={false} strokeOpacity={0.6} name={`Sim ${i+1}`} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Drawdown Distribution */}
      <div style={s.chart}>
        <h2 style={s.chartTitle}>Max Drawdown Verteilung</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={r.drawdown_distribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2539" />
            <XAxis dataKey="percentile" tick={{ fill: '#64748b' }} label={{ value: 'Perzentil', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 12 }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} label={{ value: 'Max Drawdown (%)', angle: -90, position: 'insideLeft', offset: 10, fill: '#64748b', fontSize: 12 }} />
            <Tooltip contentStyle={{ background: '#13172b', border: '1px solid #1e2539', borderRadius: 8 }}
              formatter={(v: any) => [`${Number(v).toFixed(1)}%`, 'Max Drawdown']} />
            <Bar dataKey="max_drawdown_pct" radius={[6, 6, 0, 0]}>
              {r.drawdown_distribution.map((d, i) => (
                <Cell key={i} fill={d.max_drawdown_pct > 30 ? '#ef4444' : d.max_drawdown_pct > 15 ? '#f59e0b' : '#22c55e'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MonteCarlo;
